//
// This file is part of the TYPO3 CMS-based extension "memorial".
// (c) Earlybird GmbH <typo3@earlybird.at>
//
import * as L from 'leaflet';
import './types/MapIcons';
import 'leaflet.markercluster';
import '@maplibre/maplibre-gl-leaflet';
import { MarkerClusterGroup } from 'leaflet'; // @see https://github.com/Leaflet/Leaflet.markercluster/issues/982#issuecomment-1731288935
import { MapFeature } from './types/MapFeature';
import { LargeMapIcon, MediumMapIcon } from "./types/MapIcons";

export default class Map {
  container: HTMLElement;
  data: { [key: string]: any } | null = null;
  id: string;
  labels: { [key: string]: { [key: string]: string } } = {
    de: {
      'details': 'Details',
      'placeType.0': 'Sonstiger Ort',
      'placeType.9': 'Standort',
      'placeType.10': 'Ehemaliger Standort',
      'resetView.title': 'Karte zentrieren',
      'victim': 'Opfer',
      'victims': 'Opfer',
      'zoomIn.title': 'Vergrößern',
      'zoomOut.title': 'Verkleinern',
    },
    en: {
      'details': 'Details',
      'placeType.0': 'Other location',
      'placeType.9': 'Location',
      'placeType.10': 'Former location',
      'resetView.title': 'center view',
      'victim': 'victim',
      'victims': 'victims',
      'zoomIn.title': 'zoom in',
      'zoomOut.title': 'zoom out',
    },
    fr: {
      'details': 'Détails',
      'placeType.0': 'Autre emplacement',
      'placeType.9': 'Emplacement',
      'placeType.10': 'Ancien emplacement',
      'resetView.title': 'vue centrale',
      'victim': 'victime',
      'victims': 'victimes',
      'zoomIn.title': 'zoom avant',
      'zoomOut.title': 'zoom arrière',
    },
    he: {
      'details': 'פרטים',
      'placeType.0': 'מיקום אחר',
      'placeType.9': 'מִקוּם',
      'placeType.10': 'מיקום לשעבר',
      'resetView.title': 'נוף מרכז',
      'victim': 'קָרבָּן',
      'victims': 'קורבנות',
      'zoomIn.title': 'לְהִתְמַקֵד',
      'zoomOut.title': 'התרחק',
    },
    ru: {
      'details': 'Подробности',
      'placeType.0': 'Другое местоположение',
      'placeType.9': 'Расположение',
      'placeType.10': 'Прежнее местоположение',
      'resetView.title': 'вид по центру',
      'victim': 'жертва',
      'victims': 'жертвы',
      'zoomIn.title': 'увеличить',
      'zoomOut.title': 'уменьшить масштаб',
    },
  };
  language: string = 'de';
  map: L.Map | null = null;
  options: { [key: string]: any } = {
    attribution: '&copy; <a href="https://stadiamaps.com/" target="_blank">Stadia Maps</a>, &copy; <a href="https://openmaptiles.org/" target="_blank">OpenMapTiles</a>, &copy; <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a>',
    attributionPrefix: '<a href="https://leafletjs.com/" target="_blank">Leaflet</a>',
    baseColor: 'default',
    center: new L.LatLng(47.6964719, 13.3457347),
    controls: true,
    fitBounds: true,
    maxZoom: 19,
    minZoom: 2,
    scrollWheelZoom: false,
    tiles: 'https://tiles-eu.stadiamaps.com/tiles/alidade_smooth/{z}/{x}/{y}{r}.png',
    zoom: 8,
  };
  resetViewControl: L.Control | null = null;
  src: string;

  constructor(targetElement: HTMLElement | string) {
    let container = (typeof targetElement === 'string') ? document.getElementById(targetElement) : targetElement;
    if (!(container instanceof HTMLElement)) {
      throw new Error('Invalid target element');
    }

    if (!container.id || container.id.trim().length === 0) {
      throw new Error('Invalid target element: empty id');
    }

    this.id = container.id;
    const dataSource = container.getAttribute('data-map-src');
    if (dataSource === null || dataSource.trim() === '') {
      throw new Error('Empty or missing \'data-map-src\' attribute for map \'' + this.id + '\'');
    }
    this.src = dataSource;
    this.container = container;

    this.initMap().then((map) => { this.map = map; this.toggleResetViewButton() });
  }

  private async initMap() {
    let response = await fetch(this.src);
    let data = await response.json();

    if (data !== null && data.type === 'FeatureCollection' && Array.isArray(data.features)) {
      this.data = data;
      // Override options
      if (data.options) {
        Object.entries(data.options).forEach(([key, value]) => {
          if (key !== 'center') {
            this.options[key] = value;
          }
        });
      }
      this.options.zoom = this.options.zoom * 1;
      this.options.minZoom = this.between(this.options.minZoom, 0, this.options.zoom) ? this.options.minZoom * 1 : this.options.zoom;
      this.options.maxZoom = this.between(this.options.maxZoom, this.options.minZoom, 19) ? this.options.maxZoom * 1 : this.options.minZoom;
      if (Array.isArray(data.options.center) && data.options.center[0] !== undefined && data.options.center[1] !== undefined) {
        this.options.center = new L.LatLng(data.options.center[0].toFixed(10), data.options.center[1].toFixed(10));
      }
      if (data.options.style) {
        delete data.options.tiles;
      }
      const language = data.meta.language;
      if (this.labels[language] !== undefined) {
        this.language = language;
      }

      delete data.options;
      this.data = data;

      return this.renderMap();
    }
    return null;
  }

  private renderMap(): L.Map | null {
    if (!this.data) {
      return null;
    }

    const map = L.map(this.id, {minZoom: this.options.minZoom, maxZoom: this.options.maxZoom, scrollWheelZoom: this.options.scrollWheelZoom, zoomControl: false});
    map.attributionControl.setPrefix(this.options.attributionPrefix);
    map.attributionControl.addAttribution(this.options.attribution);
    if (this.options.baseColor) {
      map.getContainer().classList.add('leaflet-container--' + this.options.baseColor);
    }

    if (this.options.style) {
      // Vector tiles
      L.maplibreGL({style: this.options.style}).addTo(map);
    } else {
      // Raster tiles
      L.tileLayer(this.options.tiles, {detectRetina: false, minZoom: this.options.minZoom, maxZoom: this.options.maxZoom}).addTo(map);
    }

    if (this.options.controls) {
      // Custom zoom control
      const zoomControl = L.control.zoom({
        zoomInText: '',
        zoomOutText: '',
        zoomInTitle: this.lll('zoomIn.title'),
        zoomOutTitle: this.lll('zoomOut.title'),
        position: 'topright'
      });
      zoomControl.addTo(map);

      // Reset view control
      const resetViewControl = new L.Control({ position: 'topright' });
      resetViewControl.onAdd = () => {
        const container = L.DomUtil.create('div', 'leaflet-control-resetview leaflet-control leaflet-bar');

        const button = L.DomUtil.create('a', 'leaflet-control-resetview-reset leaflet-disabled', container);
        let buttonTitle = this.lll('resetView.title');
        button.href = '#';
        button.title = buttonTitle;
        button.setAttribute('role', 'button');
        button.setAttribute('aria-label', buttonTitle);
        button.setAttribute('aria-disabled', 'true');

        button.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (this.map && button.getAttribute('aria-disabled') !== 'true') {
            // Note: setView does sometimes 'round' the given LatLng :/
            this.map.setView(this.options.center, this.options.zoom, {animate: true, duration: .15});
          }
        });

        return container;
      };
      resetViewControl.addTo(map);
      this.resetViewControl = resetViewControl;
    }

    if (this.options.bbox && this.options.bbox.length === 4) {
      const corner1 = L.latLng(this.options.bbox[0], this.options.bbox[1]),
        corner2 = L.latLng(this.options.bbox[2], this.options.bbox[3]),
        mapBounds = L.latLngBounds(corner1, corner2);
      this.options.center = mapBounds.getCenter();
      this.options.zoom = map.getBoundsZoom(mapBounds);
      this.options.fitBounds = false;
    }

    if (this.data.features.length === 1) {
      // Single marker (centered by API)
      const feature = new MapFeature(this.data.features[0]);
      const marker = this.getMarker(feature).addTo(map);
      if (feature.properties && feature.properties['open-popup']) {
        marker.openPopup();
      }
    } else {
      // Multiple markers
      const markerCluster = new MarkerClusterGroup({ showCoverageOnHover: false });
      this.data.features.forEach((featureData: object) => {
        const feature = new MapFeature(featureData);
        if (feature.geometry.type && feature.geometry.type === 'Point') {
          const marker = this.getMarker(feature);
          if (marker.getLatLng().lat !== 0 && marker.getLatLng().lng !== 0) {
            markerCluster.addLayer(marker);
          }
        }
      });

      // Zoom to map bounds (if bounds were not set by options)
      if (this.options.fitBounds) {
        const markerBounds = markerCluster.getBounds().pad(.05);
        this.options.center = markerBounds.getCenter();
        this.options.zoom = map.getBoundsZoom(markerBounds);
      }

      map.addLayer(markerCluster);
    }

    // Re-enable reset view button on move
    map.addEventListener('moveend', () => {
      this.toggleResetViewButton();
    });

    // Finally set the view (center/zoom)
    map.setView(this.options.center, this.options.zoom, {animate: false});
    return map;
  }

  private getMarker(feature: MapFeature): L.Marker {
    const size = (feature.properties['marker-size'] === 'large') ? 'large' : 'medium';
    const params = new URLSearchParams({'marker-size': size});
    ['marker-color', 'fill', 'fill-opacity', 'stroke', 'stroke-opacity'].forEach(function(property) {
      if (feature.properties[property]) {
        params.append(property, feature.properties[property]);
      }
    });
    const iconUrl = '/api/map/marker' + '?' + params.toString();

    const markerIcon = (size === 'medium') ? new MediumMapIcon({iconUrl: iconUrl}) : new LargeMapIcon({iconUrl: iconUrl});
    const markerColor = feature.properties['marker-color'] ?? 'default';
    let marker = new L.Marker(feature.geometry.coordinates, {icon: markerIcon, title: feature.properties.title});

    let tooltipContent = this.renderTooltipContent(feature);
    if (tooltipContent !== '') {
      marker.bindTooltip(tooltipContent,{permanent: true, direction: 'bottom', className: 'leaflet-place-label leaflet-place-label--' + size + ' leaflet-place-label--' + markerColor, offset: [0, -38]}).openTooltip();
    }

    const popupContent = this.renderPopupContent(feature);
    if (popupContent !== '') {
      marker.bindPopup(popupContent);
    }

    return marker;
  }

  private renderPopupContent(feature: MapFeature): string {
    let content = '';
    if (feature.type && feature.type === 'Feature') {
      if (feature.properties.title && feature.properties.title.trim() !== '') {
        content = '<strong>' + this.escape(feature.properties.title) + '</strong>'
      }

      // Organization
      if (feature.properties.type && feature.properties.type === 'Organization') {
        if (feature.properties.placeType !== 9) {
          let placeTypeLabel = this.lll('placeType.' + feature.properties.placeType, '');
          if (placeTypeLabel !== '') {
            content = content + '<p class="small">' + this.escape(placeTypeLabel) + '</p>';
          }
        }
        if (feature.properties.address) {
          content = content + '<p>' + this.escape(feature.properties.address) + '</p>';
        }
      }

      if (feature.properties.count && feature.properties.count > 0) {
        let victimsLabel = this.lll('victim');
        if (feature.properties.count > 1) {
          victimsLabel = this.lll('victims');
        }
        content = content + '<p>' + feature.properties.count + ' ' + this.escape(victimsLabel) + '</p>';
      }
      if (feature.properties.url) {
        content = content + '<p><a href="' + encodeURI(feature.properties.url) + '">' + this.escape(this.lll('details')) + '</a></p>';
      }
    }
    return content;
  }

  private renderTooltipContent(feature: MapFeature): string {
    let content = '';
    if (feature.type && feature.type === 'Feature') {
      let count = '';
      let label = '';
      if (feature.properties.count) {
        count = (feature.properties.count > 99) ? '++' : feature.properties.count;
      }
      if (feature.properties.shortTitle) {
        label = feature.properties.shortTitle;
      } else if (feature.properties.title) {
        label = feature.properties.title;
      }
      // @todo: Add type specific tooltip (label) rendering
      // Organization
      if (feature.properties.type && feature.properties.type === 'Organization') {
        if (feature.properties.placeType !== undefined && feature.properties.placeType !== 9) {
          label = '';
        }
      }
      // Place
      if (feature.properties.type && feature.properties.type === 'Place') {
        label = '';
      }
      content = '<span class="counter">' + this.escape(count) + '</span><span class="label">' + this.escape(label) + '</span>';
    }
    return content;
  }

  private between(x: any, min: any, max: any): boolean {
    try {
      x = x * 1;
      min = min * 1;
      max = max * 1;
      return x >= min && x <= max;
    } catch (e) {}
    return false;
  }
  private escape(value: any) {
    try {
      value = value.toString();
      return value.replace(/&/g, "&amp;").replace(/>/g, "&gt;").replace(/</g, "&lt;").replace(/"/g, "&quot;");
    } catch (e) {}
    return '';
  }

  private lll(key: string, defaultValue: string|null = null): string {
    defaultValue = defaultValue === null ? key : defaultValue;
    let label: string = this.labels[this.language][key] ?? defaultValue;
    try {
      return label.toString();
    } catch (e) {}
    return '';
  }

  private isCentered(): boolean {
    if (this.map) {
      // Compare zoom
      if (this.options.zoom !== this.map.getZoom()) {
        return false;
      }
      // Compare lat/lng
      if (this.options.center && 'lat' in this.options.center && 'lng' in this.options.center) {
        const precision = 4;
        const initialCenter = [this.options.center.lat.toFixed(precision), this.options.center.lng.toFixed(precision)].toString();
        const currentCenter = [this.map.getCenter().lat.toFixed(precision), this.map.getCenter().lng.toFixed(precision)].toString();
        return initialCenter === currentCenter;
      }
    }
    // If anything went wrong (undefined map) we assume we still have the initial view
    return true;
  }

  private toggleResetViewButton(): void {
    if (this.map && this.resetViewControl) {
      const container = this.resetViewControl.getContainer();
      if (container) {
        const button = container.getElementsByClassName('leaflet-control-resetview-reset')[0];
        if (button) {
          if (this.isCentered()) {
            button.setAttribute('aria-disabled', 'true');
            button.classList.add('leaflet-disabled');
          } else {
            button.setAttribute('aria-disabled', 'false');
            button.classList.remove('leaflet-disabled');
          }
        }
      }
    }
  }
}
